/**
 * Aritmética decimal exacta para montos de Hacienda (5 decimales).
 *
 * Los montos se representan como `bigint` escalados por 10^5 ("unidades"), así
 * ₡1 234,56789 = 123456789n. Nada de flotantes en el cálculo: el mismo resultado
 * en la web (vista previa) y en el API (valor oficial que va al XML).
 */
export const SCALE = 5;
const FACTOR = 10n ** BigInt(SCALE);
/** Convierte número o texto decimal a unidades, redondeando mitad hacia afuera. */
export function toUnits(value) {
    if (value === null || value === undefined || value === '')
        return 0n;
    if (typeof value === 'bigint')
        return value * FACTOR;
    const text = typeof value === 'number' ? numberToPlain(value) : value.trim();
    const match = /^([+-])?(\d*)(?:\.(\d*))?$/.exec(text);
    if (!match)
        throw new Error(`Monto inválido: ${value}`);
    const negative = match[1] === '-';
    const whole = match[2] || '0';
    const fraction = (match[3] ?? '').padEnd(SCALE + 1, '0');
    let units = BigInt(whole) * FACTOR + BigInt(fraction.slice(0, SCALE));
    if (Number(fraction[SCALE]) >= 5)
        units += 1n;
    return negative ? -units : units;
}
/** Evita la notación exponencial de Number#toString (1e-7, 1e21). */
function numberToPlain(value) {
    if (!Number.isFinite(value))
        throw new Error(`Monto inválido: ${value}`);
    return Math.abs(value) < 1e21 ? value.toFixed(SCALE + 1) : BigInt(Math.round(value)).toString();
}
/** División entera con redondeo mitad hacia afuera. */
function divRound(numerator, denominator) {
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const q = (n * 2n + d) / (d * 2n);
    return negative ? -q : q;
}
export const add = (...values) => values.reduce((a, b) => a + b, 0n);
export const sub = (a, b) => a - b;
/** a × b, ambos en unidades. */
export function mul(a, b) {
    return divRound(a * b, FACTOR);
}
/** a ÷ b, ambos en unidades. */
export function div(a, b) {
    if (b === 0n)
        throw new Error('División entre cero');
    return divRound(a * FACTOR, b);
}
/** a × porcentaje / 100 (porcentaje en unidades, p. ej. toUnits(13)). */
export function percent(a, pct) {
    return divRound(a * pct, FACTOR * 100n);
}
export const min = (a, b) => (a < b ? a : b);
export const max = (a, b) => (a > b ? a : b);
/** Texto con `places` decimales, punto y sin miles (formato del XML). */
export function formatUnits(units, places = SCALE) {
    const rounded = places === SCALE ? units : divRound(units, 10n ** BigInt(SCALE - places));
    const negative = rounded < 0n;
    const abs = negative ? -rounded : rounded;
    const factor = 10n ** BigInt(places);
    const whole = abs / factor;
    const fraction = (abs % factor).toString().padStart(places, '0');
    return `${negative ? '-' : ''}${whole}${places > 0 ? `.${fraction}` : ''}`;
}
export function toNumber(units) {
    return Number(formatUnits(units));
}
/** Redondea un número a 5 decimales de forma exacta (atajo para quien trabaja con `number`). */
export function round5(value) {
    return toNumber(toUnits(value));
}
//# sourceMappingURL=money.js.map